// @flow

import * as R from 'ramda';

import { HttpService } from '~/services';
import { parseBool, renameKeys } from '~/helpers';
import { SETTINGS } from '~/gql';

const REFERRALS_PAGE_SIZE = 100;
const BREADCRUMBS_PAGE_SIZE = 50;

export const resolvers = {
  Query: {
    referralLink: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?ReferralLink> => {
      // Get referral link
      const {
        data: { Invitation },
      } = await HttpService.client.get('/MyNetwork/InvitationQr');

      const referralLink: ReferralLink = {
        value: R.propOr('', 'URL', Invitation),
        __typename: 'ReferralLink',
      };

      cache.writeData({ data: { referralLink } });

      return referralLink;
    },
    networkCityDetails: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?NetworkCityDetails> => {
      const {
        data: { CityDetails },
      } = await HttpService.client.get('/MyNetwork/GetCityDetails');

      const networkCityDetails = R.pipe(
        renameKeys({
          Name: 'name',
          ImageSrc: 'logoSrc',
        }),
        R.assoc('__typename', 'NetworkCityDetails'),
      )(CityDetails);

      cache.writeData({ data: { networkCityDetails } });

      return networkCityDetails;
    },
    networkGenerationCount: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?NetworkGenerationCount> => {
      // Get breadcrumbs data
      const {
        data: { MemberTreeGen },
      } = await HttpService.client.get('/MyNetwork/GetMemberTreeGenCount');

      const networkGenerationCount: NetworkGenerationCount = {
        value: R.pipe(R.last, R.prop('Count'))(MemberTreeGen) || 0,
        __typename: 'NetworkGenerationCount',
      };

      cache.writeData({ data: { networkGenerationCount } });

      return networkGenerationCount;
    },
    networkSearchParameters: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      // $FlowIssue
      __, // eslint-disable-line no-unused-vars
      { cache }: ApolloCache,
    ): Promise<?NetworkSearchParameters> => {
      const [
        {
          data: { ParameterList },
        },
        {
          data: { ParameterCondList },
        },
      ] = await Promise.all(
        [
          '/MyNetwork/GetParameter',
          '/MyNetwork/GetParameterCondition',
        ].map((url) => HttpService.client.get(url)),
      );

      const networkSearchParameters = {
        conditions: R.map(
          R.pipe(R.assoc('__typename', 'NetworkSearchParametersItem')),
          ParameterCondList,
        ),
        parameters: R.map(
          R.pipe(R.assoc('__typename', 'NetworkSearchParametersItem')),
          ParameterList,
        ),
        __typename: 'NetworkSearchParameters',
      };

      cache.writeData({ data: { networkSearchParameters } });

      return networkSearchParameters;
    },
    networkBreadcrumbs: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?(NetworkBreadcrumbsItem[])> => {
      const { page, userName, referralName, generationLevel } = props;
      const isGenerationBreadcrumbs =
        generationLevel > 1 && userName === referralName;
      const endpoint = isGenerationBreadcrumbs
        ? '/MyNetwork/GetGenerationBreadcrumb'
        : '/MyNetwork/GetNetworkBreadcrumb';
      const params = isGenerationBreadcrumbs
        ? { Generation: generationLevel }
        : {
            PageNum: page,
            PageSize: BREADCRUMBS_PAGE_SIZE,
            Username: userName !== referralName ? referralName : null,
          };

      // Get network breadcrumbs
      const {
        data: {
          GenerationList,
          NetworkList,
          PageCount,
          PageNum,
          PageSize,
          RecordCount,
        },
      } = await HttpService.client.get(endpoint, {
        params,
      });

      const breadcrumbs = isGenerationBreadcrumbs
        ? R.map(
            R.pipe(
              renameKeys({
                Description: 'name',
                Generation: 'generationLevel',
              }),
              R.assoc('__typename', 'NetworkBreadcrumbsItem'),
            ),
            GenerationList,
          )
        : R.addIndex(R.map)(
            R.pipe(
              (props, i) => ({
                ...props,
                generationLevel: i + 2,
              }),
              renameKeys({ Username: 'name' }),
              R.assoc('__typename', 'NetworkBreadcrumbsItem'),
            ),
            NetworkList,
          );

      const networkBreadcrumbs = {
        breadcrumbs,
        pageCount: PageCount || null,
        pageNum: PageNum || null,
        pageSize: PageSize || null,
        recordCount: RecordCount || null,
        type: isGenerationBreadcrumbs ? 'generation' : 'network',
        __typename: 'NetworkBreadcrumbs',
      };

      cache.writeData({ data: { networkBreadcrumbs } });

      return networkBreadcrumbs;
    },
    networkUserProfile: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { locale, userName }: NetworkUserQueryInput,
      { cache }: ApolloCache,
    ): Promise<?NetworkUser> => {
      const {
        data: {
          MemberDetails: {
            CityScore,
            CityScorePercentage,
            CityScorePercentage2,
            Contribution,
            HasDepositBalance,
            MemberQuantity,
            DepositWalletAddress: depositBTCAddress,
            UserGuid: id,
            Username: name,
            UserRole: role,
          },
        },
      } = await HttpService.client.get('/MyNetwork/GetNetworkDetails', {
        params: {
          Username: userName,
        },
      });

      const networkUserProfile: NetworkUser = {
        id,
        name,
        memberQuantity: parseFloat(MemberQuantity),
        cityScore: parseFloat(CityScore),
        cityScorePtc: parseFloat(
          locale === 'en' ? CityScorePercentage : CityScorePercentage2,
        ),
        contribution: parseFloat(Contribution),
        depositBTCAddress,
        role,
        hasDeposit: parseBool(HasDepositBalance),
        __typename: 'NetworkUser',
      };

      cache.writeData({
        data: { networkUserProfile },
        variables: { locale, userName },
      });

      return networkUserProfile;
    },
    networkReferrals: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      props: RouteNavigationInputProps,
      { cache }: ApolloCache,
    ): Promise<?(NetworkReferralsItem[])> => {
      const { generationLevel } = props;

      const isExactSearch = ({ filter }) =>
        R.propOr('advanced', 'search', filter) === 'exact';

      const isMemberTreeList = ({ filter }) =>
        R.has('name', filter) || R.has('rule', filter);

      const isNetworkByGeneration = ({
        userName,
        referralName,
        generationLevel,
      }) => generationLevel > 1 && userName === referralName;

      const endpoint = R.cond([
        [isExactSearch, () => '/MyNetwork/ExactSearch'],
        [isMemberTreeList, () => '/MyNetwork/MemberTreeList'],
        [isNetworkByGeneration, () => '/MyNetwork/GetNetworkByGeneration'],
        [R.T, () => '/MyNetwork/GetNetworkReferrals'],
      ])(props);

      const params = R.cond([
        [
          isExactSearch,
          ({ filter: { name } }) => ({
            Username: name,
          }),
        ],
        [
          isMemberTreeList,
          ({ page, filter, sortBy, referralName, generationLevel }) => {
            let params = {};

            if (R.has('rule', filter)) {
              const rules = R.tryCatch(JSON.parse, () => {})(filter.rule);

              if (rules.value) {
                params = {
                  Parameter: rules.parameter,
                  ParameterCondition: rules.condition,
                  ParameterValue: rules.value,
                };
              }
            }

            if (!isNetworkByGeneration) {
              params = {
                ...params,
                Referral: referralName,
              };
            }

            return {
              ...params,
              Generation: generationLevel,
              PageNum: page,
              PageSize: REFERRALS_PAGE_SIZE,
              SortBy: sortBy || 'Ascending',
              Username: filter.name,
            };
          },
        ],
        [
          isNetworkByGeneration,
          ({ generationLevel, page, sortBy }) => ({
            Generation: generationLevel,
            PageNum: page,
            PageSize: REFERRALS_PAGE_SIZE,
            SortBy: sortBy || 'Ascending',
          }),
        ],
        [
          R.T,
          ({ page, referralName, sortBy, userName }) => ({
            PageNum: page,
            PageSize: REFERRALS_PAGE_SIZE,
            SortBy: sortBy || 'Ascending',
            Username: userName !== referralName ? referralName : null,
          }),
        ],
      ])(props);

      // Get network referrals
      let {
        data: {
          PageCount,
          PageNum,
          PageSize,
          RecordCount,
          ReferralsList,
          ExactSearchList,
          CurrentGeneration,
          TotalGeneration,
        },
      } = await HttpService.client.get(endpoint, {
        params,
      });

      const networkReferrals = {
        pageCount: PageCount,
        pageNum: PageNum,
        pageSize: PageSize,
        recordCount: RecordCount,
        referrals: R.map(
          R.pipe(
            renameKeys({
              CitizenType: 'citizenType',
              CitizenTypeName: 'citizenTypeName',
              UserRole: 'role',
              Username: 'name',
              HasDepositBalance: 'hasDeposit',
            }),
            R.evolve({
              hasDeposit: parseBool,
            }),
            R.assoc('generationLevel', generationLevel + 1),
            R.assoc('__typename', 'NetworkReferralsItem'),
          ),
          isExactSearch(props) ? ExactSearchList : ReferralsList,
        ),
        currentGeneration: CurrentGeneration || null,
        totalGeneration: TotalGeneration || null,
        type: R.cond([
          [isMemberTreeList, () => 'search'],
          [isNetworkByGeneration, () => 'generation'],
          [R.T, () => 'network'],
        ])(props),
        __typename: 'NetworkReferrals',
      };

      cache.writeData({ data: { networkReferrals }, variables: props });

      return networkReferrals;
    },
  },
  Mutation: {
    inviteByEmailsMutation: async (
      // $FlowIssue
      _, // eslint-disable-line no-unused-vars
      { values }: NetworkInviteByEmailsInputMutation,
      { cache }: ApolloCache,
    ): Promise<MessagePayload> => {
      const { settings }: SettingsQuery = cache.readQuery({
        query: SETTINGS,
      });

      const params = {
        ...values,
        // $FlowIssue
        locale: R.propOr('en', 'locale', settings),
      };

      await HttpService.client.post('/', params, {
        baseURL: process.env.REACT_APP_LAMBDA_ENDPOINT || '',
        headers: {
          Authorization: 'none',
          'Content-Type': 'application/json',
        },
      });

      return {
        code: 0,
        message: 'Done',
        __typename: 'MessagePayload',
      };
    },
  },
};
